import {
  siCalendly,
  siClaude,
  siCloudflare,
  siGmail,
  siGoogleads,
  siGoogleanalytics,
  siGooglecalendar,
  siGooglesearchconsole,
} from "simple-icons";

/**
 * Brand marks for the integrations catalog - real logos where simple-icons
 * carries them, hand-drawn equivalents for brands it dropped for trademark
 * policy (LinkedIn, Microsoft) and a lettermark for GPT. Marks identify
 * interoperability targets only.
 */

export interface BrandMarkDef {
  title: string;
  hex: string;
  /** simple-icons 24x24 path. */
  path?: string;
  /** Custom node instead of a path (multi-color or lettermark). */
  custom?: React.ReactNode;
}

const fromSimple = (icon: { title: string; hex: string; path: string }): BrandMarkDef => ({
  title: icon.title,
  hex: `#${icon.hex}`,
  path: icon.path,
});

/** LinkedIn "in" tile (simple-icons removed the mark; drawn by hand). */
const linkedin: BrandMarkDef = {
  title: "LinkedIn",
  hex: "#0A66C2",
  custom: (
    <g>
      <rect x="1" y="1" width="22" height="22" rx="4" fill="#0A66C2" />
      <text
        x="12"
        y="17.5"
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontWeight="700"
        fontSize="13"
        fill="#ffffff"
      >
        in
      </text>
    </g>
  ),
};

/** Microsoft four-square mark (drawn by hand). */
const microsoft: BrandMarkDef = {
  title: "Microsoft 365",
  hex: "#737373",
  custom: (
    <g>
      <rect x="2.5" y="2.5" width="9" height="9" fill="#F25022" />
      <rect x="12.5" y="2.5" width="9" height="9" fill="#7FBA00" />
      <rect x="2.5" y="12.5" width="9" height="9" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="9" height="9" fill="#FFB900" />
    </g>
  ),
};

/** OpenAI GPT lettermark tile (mark not redistributable). */
const gpt: BrandMarkDef = {
  title: "OpenAI (GPT)",
  hex: "#10A37F",
  custom: (
    <g>
      <rect x="1" y="1" width="22" height="22" rx="11" fill="#10A37F" />
      <text
        x="12"
        y="15.5"
        textAnchor="middle"
        fontFamily="system-ui, sans-serif"
        fontWeight="700"
        fontSize="8.5"
        fill="#ffffff"
      >
        GPT
      </text>
    </g>
  ),
};

/** Integration key → the brand marks shown on its card. */
export const INTEGRATION_BRANDS: Record<string, BrandMarkDef[]> = {
  ga4: [fromSimple(siGoogleanalytics)],
  gsc: [fromSimple(siGooglesearchconsole)],
  googleAds: [fromSimple(siGoogleads)],
  linkedinAds: [linkedin],
  calendly: [fromSimple(siCalendly), fromSimple(siGooglecalendar)],
  emailSync: [fromSimple(siGmail), microsoft],
  ai: [fromSimple(siClaude), gpt],
  turnstile: [fromSimple(siCloudflare)],
};

export const BrandMark = ({ def, size = 26 }: { def: BrandMarkDef; size?: number }) => (
  <span
    title={def.title}
    className="inline-flex items-center justify-center rounded-md border border-border bg-white shrink-0"
    style={{ width: size + 10, height: size + 10 }}
  >
    <svg
      role="img"
      aria-label={def.title}
      viewBox="0 0 24 24"
      width={size}
      height={size}
    >
      {def.custom ?? <path d={def.path} fill={def.hex} />}
    </svg>
  </span>
);

export const BrandMarkGroup = ({ defs }: { defs: BrandMarkDef[] }) => (
  <span className="inline-flex items-center gap-1.5">
    {defs.map((d) => (
      <BrandMark key={d.title} def={d} />
    ))}
  </span>
);
